import React, { useState, useEffect } from 'react';
import { MdEmail, MdLock, MdVisibility, MdVisibilityOff, MdApps } from 'react-icons/md';
import API from '../api';
import { LanguageSelector, useLanguage } from '../i18n';
import { useSiteSettings } from '../hooks/useSiteSettings';

// A system-specific login link looks like /login?system=acme-co, which
// prefills the System code field below — but that field is also editable,
// so someone on the plain /login page can type a code directly instead of
// needing the special link. With no code entered (the normal case), login
// behaves exactly as it always has.
// Where to go after signing in: a page inside the app that the visitor was heading to
// (for example from a link in an email). Only paths inside /app/ are accepted.
function nextPath() {
    const next = new URLSearchParams(window.location.search).get('next') || '';
    return next.startsWith('/app/') && !next.startsWith('//') && !next.includes('://') ? next : '';
}

export default function Login() {
    const { t } = useLanguage();
    const { systemName, logoUrl } = useSiteSettings();
    const [form, setForm] = useState({ email: '', password: '' });
    const [systemSlug, setSystemSlug] = useState(() => new URLSearchParams(window.location.search).get('system') || '');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPass, setShowPass] = useState(false);

    // Already signed in and following a link to a page in the app: go straight there.
    useEffect(() => {
        if (localStorage.getItem('fms_token') && nextPath()) window.location.replace(nextPath());
    }, []);

    const handleSubmit = async e => {
        e.preventDefault();
        setLoading(true); setError('');
        try {
            const trimmedSystem = systemSlug.trim();
            const payload = trimmedSystem ? { ...form, system: trimmedSystem } : form;
            const { data } = await API.post('/auth/login', payload);
            localStorage.setItem('fms_token', data.token);
            localStorage.setItem('fms_user', JSON.stringify(data.user));
            // Route based on role. Developers land on their own /app/developer
            // area (Layout.js's sidebar only gives them Developer/Users/Settings
            // links, not Dashboard) — sending them to /app/dashboard instead
            // used to work by accident until ManagerRoute bounced them straight
            // to /app/my-tasks, since ManagerRoute only allow-lists admin/manager.
            if (nextPath()) {
                window.location.href = nextPath();
            } else if (data.user.role === 'employee') {
                window.location.href = '/app/my-tasks';
            } else if (data.user.role === 'developer') {
                window.location.href = '/app/developer';
            } else {
                window.location.href = '/app/dashboard';
            }
        } catch (err) {
            setError(err.response?.data?.error || t('login.failed'));
        } finally { setLoading(false); }
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #edf5ff 0%, #dfeeff 30%, #b8d6ff 60%, #0b5ed7 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px'
        }}>
            <div style={{ width: '100%', maxWidth: 400 }}>
                <div style={{ textAlign: 'center', marginBottom: 18 }}>
                    <img src={logoUrl || '/miti-logo.png'} alt={`${systemName || 'MITI FMS'} logo`} style={{ width: 60, height: 60, borderRadius: 14, objectFit: 'contain', marginBottom: 10, background: '#fff', padding: 4, boxShadow: '0 10px 24px rgba(11, 94, 215, 0.2)' }} />
                    <h1 style={{ color: '#0d1b2a', fontWeight: 800, fontSize: 28, lineHeight: 1.1, margin: '0 0 5px', letterSpacing: 0 }}>{systemName || 'MITI FMS'}</h1>
                    <p style={{ color: '#29425e', margin: 0, fontSize: 14, fontWeight: 600 }}>{t('header.facilityManagement')}</p>
                </div>

                <div style={{ background: 'rgba(255,255,255,0.97)', borderRadius: 14, padding: '26px 24px 22px', boxShadow: '0 22px 48px rgba(9, 32, 64, 0.18)', border: '1px solid rgba(11,94,215,0.08)' }}>
                    <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: 12, marginBottom: 5 }}>
                        <h2 style={{ flex: 1, fontWeight: 750, fontSize: 19, lineHeight: 1.25, margin: 0, color: '#0d1b2a' }}>{t('login.title')}</h2>
                        <LanguageSelector style={{ flexShrink: 0 }} />
                    </div>
                    <p style={{ color: '#52657d', fontSize: 13, margin: '0 0 22px' }}>
                        {t('login.subtitle')}
                    </p>

                    {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}

                    <form onSubmit={handleSubmit}>
                        <div className="mb-3">
                            <label className="form-label" style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{t('login.email')}</label>
                            <div style={{ position: 'relative' }}>
                                <MdEmail style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 18 }} />
                                <input type="email" className="form-control" style={{ minHeight: 42, paddingLeft: 38 }} autoComplete="username"
                                    value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
                            </div>
                        </div>

                        <div className="mb-2">
                            <label className="form-label" style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{t('login.password')}</label>
                            <div style={{ position: 'relative' }}>
                                <MdLock style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 18 }} />
                                <input type={showPass ? 'text' : 'password'} className="form-control" autoComplete="current-password"
                                    style={{ minHeight: 42, paddingLeft: 38, paddingRight: 42 }}
                                    value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required />
                                <button type="button" aria-label={showPass ? t('account.hidePassword') : t('account.showPassword')} onClick={() => setShowPass(!showPass)} style={{
                                    position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                                    border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4
                                }}>
                                    {showPass ? <MdVisibilityOff size={18} /> : <MdVisibility size={18} />}
                                </button>
                            </div>
                        </div>

                        <div className="mb-2">
                            <label className="form-label" style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{t('login.systemCode') || 'System code (optional)'}</label>
                            <div style={{ position: 'relative' }}>
                                <MdApps style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 18 }} />
                                <input type="text" className="form-control" style={{ minHeight: 42, paddingLeft: 38 }} autoComplete="off"
                                    value={systemSlug} onChange={e => setSystemSlug(e.target.value)} placeholder="e.g. acme-co" />
                            </div>
                            <div style={{ color: '#94a3b8', fontSize: 11.5, marginTop: 5 }}>
                                {t('login.systemCodeHint') || 'Leave blank unless your organization gave you a system code.'}
                            </div>
                        </div>

                        <div style={{ textAlign: 'right', marginBottom: 18 }}>
                            <a href={systemSlug.trim() ? `/forgot-password?system=${encodeURIComponent(systemSlug.trim())}` : '/forgot-password'} style={{ fontSize: 13, color: '#0b5ed7', fontWeight: 600 }}>{t('login.forgotPassword')}</a>
                        </div>

                        <button type="submit" className="btn btn-primary w-100" disabled={loading}
                            style={{ minHeight: 44, padding: 10, fontWeight: 700, fontSize: 14 }}>
                            {loading ? <><span className="spinner-border spinner-border-sm me-2" />{t('login.signingIn')}</> : t('login.signIn')}
                        </button>
                    </form>

                    <div style={{ textAlign: 'center', marginTop: 18, fontSize: 13.5, color: '#64748b' }}>
                        {t('login.newHere')} <a href="/signup" style={{ color: '#0b5ed7', fontWeight: 700 }}>{t('login.createAccount')}</a>
                    </div>

                </div>
            </div>
        </div>
    );
}
